import { z } from 'zod';

export const SessionSchema = z
  .object({
    clientIp: z.string().optional(),
    landingPageUrl: z.string().optional(),
    customerId: z.number().int().optional(),
    customerEmail: z.string().optional(),
    customerPhone: z.string().optional(),
    customerFirstName: z.string().optional(),
    customerLastName: z.string().optional(),
    customerPostcode: z.string().optional(),
    customerCity: z.string().optional(),
    customerStreet: z.string().optional(),
    customerOrdersCount: z.number().int().optional(),
    customerSubscriptionStatus: z.string().optional(),
    customerSavingsPlan: z.string().optional(),
    nutritionalNeeds: z.string().optional(),
    breed: z.string().optional(),
    petProfile: z.string().optional(),
    allSegments: z.string().optional(),
    lastSegment: z.string().optional(),
    petType: z.string().optional(),
  })
  .strict();

export type Session = z.infer<typeof SessionSchema>;

export const SessionUpdateSchema = SessionSchema.partial();

export const NoPayloadSchema = z.object({}).strict();

export const ElementEventPayloadSchema = z
  .object({
    element: z
      .union([z.string(), z.array(z.string())])
      .transform(function serializeElementName(element): string {
        return `#${typeof element === 'string' ? element : element.join(' #')}`;
      }),
    list: z.string().optional(),
  })
  .strict();

export const LabelPayloadSchema = z.object({ label: z.string() }).strict();
export const TitlePayloadSchema = z.object({ title: z.string() }).strict();
export const IdPayloadSchema = z.object({ id: z.string() }).strict();

const VariantIdSchema = z.union([z.string(), z.number()]).transform((value) => value.toString());

const ProductIdSchema = z.union([z.string(), z.number()]).transform((value) => value.toString());

export const ProductSchema = z
  .object({
    productTitle: z.string(),
    productId: ProductIdSchema,
    productType: z.string(),
    tags: z.string(),
  })
  .strict();

export type Product = z.infer<typeof ProductSchema>;

export const ListItemSchema = z
  .object({
    list: z.string(),
    position: z.number(),
  })
  .strict();

export type ListItem = z.infer<typeof ListItemSchema>;

export const ProductListItemSchema = ProductSchema.extend({
  list: z.string(),
  position: z.number(),
}).strict();

export const ProductListItemWithVariantSchema = ProductListItemSchema.extend({
  variantId: VariantIdSchema,

  // TODO: Low prio. Check if this value is passed.
  // Looks like it's a leftower after migration from shopify api
  variantPrice: z.string().optional(),
}).strict();

export const ProductWithVariantSchema = ProductSchema.extend({
  variantId: VariantIdSchema,
}).strict();

export const ProductWithTabSchema = ProductSchema.extend({
  tabName: z.string(),
}).strict();

export const VariantSchema = ProductSchema.extend({
  variantTitle: z.string(),
  variantId: VariantIdSchema,
  sku: z.string(),
}).strict();

export const CheckoutItemSchema = VariantSchema.extend({
  quantity: z.number(),
  price: z.number(),
  taxRate: z.number(),
  interval: z.string().optional(),
}).strict();

export type CheckoutItem = z.infer<typeof CheckoutItemSchema>;

export const CheckoutItemWithoutTaxSchema = CheckoutItemSchema.omit({
  taxRate: true,
});

export const CheckoutItemsOnlySchema = z
  .object({
    items: z.array(CheckoutItemSchema),
  })
  .strict();

export const PaymentChannelSchema = z
  .object({
    gateway: z.string(),
    method: z.string(),
  })
  .strict();

export type PaymentChannel = z.infer<typeof PaymentChannelSchema>;

export const CheckoutDiscountSchema = z
  .object({
    type: z.string(),
    value: z.number(),
    code: z.string().optional(),
  })
  .strict();

export const CheckoutShippingSchema = z
  .object({
    type: z.string(),
    price: z.number(),
    vat: z.number(),
  })
  .strict();

export const CheckoutSchema = z
  .object({
    requestId: z.string(),
    customer: z
      .object({
        id: z.number(),
        email: z.string(),
        ordersCount: z.number(),
      })
      .strict(),
    totalAmount: z.number(),
    totalVat: z.number(),
    items: z.array(CheckoutItemSchema),
    itemsTotal: z.number(),
    discount: CheckoutDiscountSchema.optional(),
    shipping: CheckoutShippingSchema.optional(),
    payment: PaymentChannelSchema.optional(),
  })
  .strict();

export type Checkout = z.infer<typeof CheckoutSchema>;

export const CheckoutCompleteSchema = CheckoutSchema.extend({
  shipping: CheckoutShippingSchema,
  payment: PaymentChannelSchema,
}).strict();

export const MarketingSchema = z.object({ marketing: z.boolean() }).strict();

export const CustomerNameSchema = z
  .object({
    firstName: z.string(),
    lastName: z.string(),
  })
  .strict();

export const CheckoutExpressShippingSchema = z
  .object({
    type: z.string(),
  })
  .strict();

export const CartIntervalSelectionSchema = z
  .object({
    type: z.string(),
  })
  .strict();

export const AnswerPayloadSchema = z
  .object({
    answer: z
      .object({
        key: z.string(),
        value: z.unknown().optional(),
      })
      .strict(),
  })
  .strict();

export const SubscriptionLabelPayloadSchema = z
  .object({
    label: z.string(),
  })
  .strict();

export const SubscriptionItemPayloadSchema = z
  .object({
    item: z
      .object({
        variantId: VariantIdSchema,
      })
      .strict(),
  })
  .strict();
